import { Container, Stack } from "@mui/material";
import { useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import ApplicationForm from "src/components/application/ApplicationForm";
import LoadingComponent from "src/components/application/LoadingComponent";
import SelectAccountType from "src/components/application/SelectAccountType";
import CustomizedSteppers from "src/components/application/Stepper";
import TransmitVerifySuccess from "src/components/application/TransmitVerifySuccess";
import AppFormLegalTerm from "src/components/application/utils/AppFormLegalTerm";
import useIgnoreBiometrics from "src/components/application/utils/useIgnoreBiometrics";
import Layout from "src/components/core/Layout";
import StyledButton from "src/components/dashboard/StyledButton";
import useSumsubStatus from "src/components/portfolio/hooks/useSumsubStatus";
import OnfidoForm from "src/components/settings/onfido/OnfidoVerification";
import OnfidoPrivacyConsent from "src/components/application/OnfidoPrivacyConsent";
import {
  setAccountSelection,
  setApplicationStep,
  setUkResumeChoiceMade,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import { useUserSlice } from "src/store/features/user.slice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
// UK Onboarding Components
import {
  UKRiskSummary,
  UKInvestorType,
  UKRestrictedInvestorStatement,
  UKHNWInvestorStatement,
  UKAppropriatenessAssessment,
  UKAssessmentResult,
  UKCoolingOffPending,
  UKFinalConfirmation,
  UKOnboardingSuccess,
  UKWelcomeBack,
  useHydrateUkOnboardingStatus,
} from "src/components/application/uk-onboarding";

export default function Application() {
  const { reviewAnswer } = useSumsubStatus();
  // If reviewAnswer is RED, then the account is pending OR action required
  const sumsumbPending = reviewAnswer === "RED";

  const onboardingState = useAppSelector((state) => state.onboarding);
  const [useDefaultTheme, toggle] = useReducer((theme: any) => !theme, true);
  const [termsAccepted, setTermsAccepted] = useReducer((terms: boolean) => !terms, false);
  const { applicationStep, accountType } = useOnboarding();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [ignoreBiometrics, _, isVerificationLoading] = useIgnoreBiometrics();

  const {
    kycSteps: { newApplicationForm, biometrics },
  } = useUserSlice();

  // Get UK onboarding state from Redux
  const { ukOnboarding } = useOnboarding();
  const {
    riskSummaryViewed: ukRiskSummaryViewed,
    investorType: ukInvestorType,
    restrictedStatement: ukRestrictedStatement,
    hnwStatement: ukHnwStatement,
    assessment: { status: assessmentStatus },
    coolingOff: { status: coolingOffStatus },
    finalConfirmation: ukFinalConfirmation,
  } = ukOnboarding;

  // Derive UK step completion from Redux state
  const ukAssessmentPassed = assessmentStatus === "passed";
  const ukAssessmentResultAcknowledged = ukOnboarding.assessment.resultAcknowledged;
  const ukCoolingOffComplete = coolingOffStatus === "completed";

  // Pause-and-come-back: whether the user has saved progress from a previous
  // session, and whether they've already made the continue/start-fresh
  // choice this session.
  const ukResumeChoiceMade = ukOnboarding.resumeChoiceMade;
  const ukCheckpoints = ukOnboarding.checkpoints;
  const hasUkSavedProgress =
    (ukCheckpoints !== null && Object.values(ukCheckpoints).some(Boolean)) ||
    ukRiskSummaryViewed ||
    !!ukInvestorType ||
    assessmentStatus !== "not_started";
  // Every re-doable step is finished — only the journey tail (cooling-off
  // countdown / final confirmation) remains, so there's nothing to choose
  // between: resume automatically instead of showing the welcome-back screen.
  const ukJourneyTailReached =
    ukCheckpoints !== null &&
    ukCheckpoints.classificationDone &&
    ukCheckpoints.assessmentDone &&
    ukCheckpoints.idVerificationDone &&
    ukCheckpoints.applicationFormDone;

  // Check if investor statement is completed based on investor type
  const ukStatementCompleted =
    (ukInvestorType === "RESTRICTED" && ukRestrictedStatement?.declarationAgreed) ||
    (ukInvestorType === "HIGH_NET_WORTH" && ukHnwStatement?.declarationAgreed);

  // Check if user is from the UK, and hydrate UK onboarding state from the
  // backend on mount so returning users land on the correct step instead of
  // repeating the assessment.
  const { isUkUser, ukStatusReady } = useHydrateUkOnboardingStatus();

  useEffect(() => {
    if (!isVerificationLoading && ukStatusReady) {
      // If all common steps completed, check for early exit
      if (biometrics && newApplicationForm) {
        if (!isUkUser) {
          dispatch(setApplicationStep("applicationSuccess"));
          trackEvent("KYC Completed", { ...onboardingState });
          return;
        }
        // UK users: also need final confirmation to be fully complete
        if (isUkUser && ukFinalConfirmation === "confirmed") {
          dispatch(setApplicationStep("applicationSuccess"));
          trackEvent("UK Onboarding Completed", { ...onboardingState });
          return;
        }
      }

      if (sumsumbPending) {
        // Redirect the user to entity-onboarding
        history.push("/entity-onboarding");
        return;
      }

      // UK pause-and-come-back: a returning user with saved progress picks
      // Continue or Start fresh before any routing happens. New UK users
      // (nothing saved yet) skip straight through — marking the choice as
      // made stops mid-session progress bouncing them back to this screen.
      if (isUkUser && !ukResumeChoiceMade) {
        if (ukJourneyTailReached) {
          // Nothing left to re-do — restore the account-type choice and let
          // the next routing pass land on cooling-off / final confirmation.
          dispatch(setUkResumeChoiceMade(true));
          if (!accountType) {
            // Entity onboarding is not available for UK clients, so a UK
            // journey is always Individual.
            dispatch(setAccountSelection("Individual"));
          }
          return;
        }
        if (hasUkSavedProgress) {
          dispatch(setApplicationStep("ukWelcomeBack"));
          return;
        }
        dispatch(setUkResumeChoiceMade(true));
      }

      if (!accountType) {
        // If account type is not selected show account type selection
        dispatch(setApplicationStep("accountType"));
        return;
      }

      // While on the account-selection step, navigation is driven by the
      // Next button in SelectAccountType (handleNext), which knows how to
      // route Individual vs Entity, the UK flow, and the AU DVS disclosure.
      // Selecting an option only sets `accountType`; don't let this effect
      // re-run and auto-advance before the user clicks Next.
      if (
        applicationStep === "accountType" ||
        applicationStep === "nonIndividualTypeSelection"
      ) {
        return;
      }

      // UK-specific routing: UK users go through Risk Summary -> Investor Type -> Statement -> Assessment BEFORE biometrics
      if (isUkUser) {
        // Don't override transient result display pages — let the user
        // read them and continue manually.
        if (applicationStep === "ukAssessmentResult") {
          return;
        }
        // Step 1: UK Risk Summary
        if (!ukRiskSummaryViewed) {
          dispatch(setApplicationStep("ukRiskSummary"));
          trackEvent("UK Onboarding Started", { ...onboardingState });
          return;
        }

        // Step 2: Investor Type Selection
        if (!ukInvestorType) {
          dispatch(setApplicationStep("ukInvestorType"));
          return;
        }

        // Step 3: Investor Statement (based on type)
        if (!ukStatementCompleted) {
          if (ukInvestorType === "RESTRICTED") {
            dispatch(setApplicationStep("ukRestrictedStatement"));
          } else if (ukInvestorType === "HIGH_NET_WORTH") {
            dispatch(setApplicationStep("ukHnwStatement"));
          }
          return;
        }

        // Step 4: Appropriateness Assessment
        if (!ukAssessmentPassed) {
          dispatch(setApplicationStep("ukAssessment"));
          return;
        }

        // Step 4b: Assessment pass result — user must acknowledge before verification
        if (!ukAssessmentResultAcknowledged) {
          dispatch(setApplicationStep("ukAssessmentResult"));
          return;
        }

        // Step 5: Biometrics (before application form, matching non-UK flow).
        // The privacy notice & consent page always precedes the Onfido pages;
        // its I Consent button advances to onfidoVerify, so don't bounce a
        // user already past the notice back onto it.
        if (!biometrics && !ignoreBiometrics) {
          if (applicationStep !== "onfidoConsent" && applicationStep !== "onfidoVerify") {
            dispatch(setApplicationStep("onfidoConsent"));
          }
          return;
        }

        // Step 6: Application form (same personal-info form non-UK users fill out)
        if (!newApplicationForm) {
          dispatch(setApplicationStep("aboutYou"));
          return;
        }

        // Step 7: Cooling-off period (after biometrics)
        if (!ukCoolingOffComplete) {
          dispatch(setApplicationStep("ukCoolingOff"));
          return;
        }

        // Step 8: Final confirmation
        if (ukFinalConfirmation !== "confirmed") {
          dispatch(setApplicationStep("ukFinalConfirmation"));
          return;
        }

        // All UK steps complete
        dispatch(setApplicationStep("applicationSuccess"));
        trackEvent("UK Onboarding Completed", { ...onboardingState });
        return;
      }

      // Non-UK users: Standard flow
      if (ignoreBiometrics || biometrics) {
        // If user has not completed onfido, show application form
        dispatch(setApplicationStep("aboutYou"));
      } else {
        // User has not completed or ignoring biometrics, show onfido verification
        dispatch(setApplicationStep("onfidoVerify"));
      }
      trackEvent("KYC Started", { ...onboardingState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newApplicationForm,
    biometrics,
    ignoreBiometrics,
    isVerificationLoading,
    sumsumbPending,
    isUkUser,
    ukRiskSummaryViewed,
    ukInvestorType,
    ukStatementCompleted,
    ukAssessmentPassed,
    ukAssessmentResultAcknowledged,
    ukCoolingOffComplete,
    ukFinalConfirmation,
    ukStatusReady,
    ukResumeChoiceMade,
    hasUkSavedProgress,
    ukJourneyTailReached,
  ]);

  // Clear account selection only on unmount (navigating away from the page).
  // The resume choice is per-visit: clearing it alongside accountType means a
  // returning visit re-runs the welcome-back decision instead of falling
  // through to account-type selection with a wiped accountType.
  useEffect(() => {
    return () => {
      dispatch(setAccountSelection(null));
      dispatch(setUkResumeChoiceMade(false));
      dispatch(setApplicationStep("loading"));
    };
  }, []);

  const isAccountSelectionStep =
    applicationStep === "accountType" || applicationStep === "nonIndividualTypeSelection";

  // Check if current step is a UK-specific step
  const isUkOnboardingStep = [
    "ukWelcomeBack",
    "ukRiskSummary",
    "ukInvestorType",
    "ukRestrictedStatement",
    "ukHnwStatement",
    "ukAssessment",
    "ukAssessmentResult",
    "ukCoolingOff",
    "ukFinalConfirmation",
  ].includes(applicationStep);

  const isMobile = useIsMobile();

  const shouldDisplayStepper =
    !isMobile &&
    applicationStep !== "accountType" &&
    applicationStep !== "nonIndividualTypeSelection" &&
    !isUkOnboardingStep; // Don't show stepper for UK-specific steps

  if (applicationStep === "applicationSuccess") {
    return (
      <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
        <Container>
          {isUkUser && ukFinalConfirmation === "confirmed" ? (
            <UKOnboardingSuccess />
          ) : (
            <TransmitVerifySuccess />
          )}
        </Container>
      </Layout>
    );
  }

  // Render UK-specific onboarding components
  const renderUkOnboardingComponent = () => {
    switch (applicationStep) {
      case "ukWelcomeBack":
        return <UKWelcomeBack />;
      case "ukRiskSummary":
        return <UKRiskSummary />;
      case "ukInvestorType":
        return <UKInvestorType />;
      case "ukRestrictedStatement":
        return <UKRestrictedInvestorStatement />;
      case "ukHnwStatement":
        return <UKHNWInvestorStatement />;
      case "ukAssessment":
        return <UKAppropriatenessAssessment />;
      case "ukAssessmentResult":
        return <UKAssessmentResult />;
      case "ukCoolingOff":
        return <UKCoolingOffPending />;
      case "ukFinalConfirmation":
        return <UKFinalConfirmation />;
      default:
        return null;
    }
  };

  return (
    <>
      <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
        <div style={{ fontSize: "14px " }}>
          {applicationStep === "loading" ? (
            <LoadingComponent message={"Getting things ready for you..."} />
          ) : isUkOnboardingStep ? (
            // UK-specific onboarding flow
            <Container>{renderUkOnboardingComponent()}</Container>
          ) : (
            <div>
              {shouldDisplayStepper && <CustomizedSteppers />}
              {applicationStep === "onfidoVerify" ? (
                // Returning user with application form completed
                <div style={{ marginTop: "16px" }}>
                  <OnfidoForm reason="Application Form" />
                </div>
              ) : applicationStep === "onfidoConsent" ? (
                // Privacy notice & consent — shown to UK clients before the
                // Onfido pages (see the UK branch of the routing effect)
                <Container>
                  <OnfidoPrivacyConsent />
                </Container>
              ) : (
                // New Application
                <Container>
                  {isAccountSelectionStep ? <SelectAccountType /> : <ApplicationForm />}
                </Container>
              )}
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}
